import React, { FC, useState } from 'react';
import { FormGroup } from '@bphxd/ds-core-react/lib/forms/form/FormGroup';
import { Button, Spinner } from '@bphxd/ds-core-react';
import { EMPTY_EMOC } from '../../../lib/constants';
import { TEmoc } from '../../../lib/types';
import { useQuery } from '@apollo/client';
import { GET_EMOC_EMOC_BY_MOC_NUMBER } from '../../../graph-queries/tar/emoc';
import { useSelectedAsset } from '../../../lib/util';
import { Form, Input, InputGroup, Label } from '@bphxd/ds-core-react';
import classes from './EmocForm.module.scss';
import clsx from 'clsx';
import { useNoticeBar } from '../../molecules/NoticeBar/NoticeBar';

const EmocForm: FC<{
  defaultValues?: TEmoc;
  onSubmit: (emoc: TEmoc) => Promise<unknown>;
  onClose: () => void;
  type: 'add' | 'edit';
  isLoading?: boolean;
}> = ({ defaultValues, onSubmit, onClose, isLoading }) => {
  const notice = useNoticeBar();
  const [mocNumber, setMocNumber] = useState(defaultValues?.mocNumber || '');
  const [inputMocNumber, setInputMocNumber] = useState(defaultValues?.mocNumber || '');
  const asset = useSelectedAsset();
  const {
    data: emocData,
    loading,
    error,
  } = useQuery(GET_EMOC_EMOC_BY_MOC_NUMBER, {
    variables: { mocNumber, assetId: asset?.assetId || -1 },
    skip: !mocNumber || !asset,
    onError: () => notice.sendError('Unable to fetch MOC details from eMOC'),
  });

  const handleSubmit: React.FormEventHandler<HTMLFormElement> = (event) => {
    // Prevent default behavior
    event.preventDefault();

    const data = new FormData(event.target as HTMLFormElement);

    const emoc: TEmoc = {
      ...EMPTY_EMOC,
      assetId: asset?.assetId || -1,
      ...Object.fromEntries(data),
    };
    if (defaultValues?.mocId) emoc.mocId = defaultValues.mocId;

    onSubmit(emoc)
      .then(onClose)
      .catch((e) => {
        console.error(e);
        notice.sendError('Unable to save MOC');
      });
  };

  const isValid = !!emocData?.emocEmoc.mocStatus && !error;

  return (
    <Form className={clsx('row g-3 px-5', classes.root)} onSubmit={handleSubmit}>
      <div className="col-md-12">
        <FormGroup>
          <Label for="emoc-number">MOC Reference Number</Label>
          <InputGroup className="mb-3">
            <Input
              placeholder="Search eMOC"
              type="text"
              name="mocNumber"
              id="emoc-number"
              required
              value={inputMocNumber}
              onChange={(e) => {
                setInputMocNumber((e.target as HTMLInputElement).value);
              }}
            />
            <Button
              disabled={!inputMocNumber}
              color="darker-outline"
              onClick={() => setMocNumber(inputMocNumber)}
            >
              Search
            </Button>
          </InputGroup>
        </FormGroup>
      </div>
      {!isValid ? (
        <div className={classes.loading}>{loading && <Spinner />}</div>
      ) : (
        <>
          <div className="col-md-12">
            <FormGroup>
              <Label for="emoc-description">Description</Label>
              <div className={classes.details}>{emocData?.emocEmoc.mocDescription || ''}</div>
            </FormGroup>
          </div>
          <div className="col-md-12">
            <FormGroup>
              <Label>Status</Label>
              <div className={classes.details}>{emocData?.emocEmoc.mocStatus || ''}</div>
            </FormGroup>
          </div>
          <div className="col-md-12">
            <FormGroup>
              <Label for="emoc-comments" className="label-optional">
                Comments
              </Label>
              <Input
                type="textarea"
                name="comments"
                id="emoc-comments"
                defaultValue={defaultValues?.comments || ''}
              />
            </FormGroup>
          </div>
        </>
      )}

      {/* Footer */}
      <div className="col-md-12 d-flex gap-5 flex-row-reverse mt-6">
        <Button
          disabled={!isValid || isLoading}
          type="submit"
          size="lg"
          className=""
          theme="darker"
          level="primary"
        >
          Save
        </Button>
        <Button size="lg" className="" theme="darker" level="tertiary" onClick={onClose}>
          Cancel
        </Button>
      </div>
    </Form>
  );
};

export default EmocForm;
